import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Globe, Search, MapPin, Star, Target, ArrowRight,
  RefreshCw, Edit3, Check, X, TrendingUp, TrendingDown, Minus, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const MODULE_KEYS = [
  { key: 'seo', label: 'SEO', icon: Search, color: '#059669' },
  { key: 'website', label: 'Website', icon: Globe, color: '#3b82f6' },
  { key: 'lokaal', label: 'Lokaal', icon: MapPin, color: '#D4A017' },
  { key: 'reputatie', label: 'Reputatie', icon: Star, color: '#8b5cf6' },
  { key: 'advertenties', label: 'Ads', icon: Target, color: '#ef4444' },
];

const ScoreBar = ({ score, color, max = 100 }) => (
  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
    <div style={{ flex: 1, height: '6px', borderRadius: '3px', background: '#f1f5f9', overflow: 'hidden' }}>
      <div style={{
        width: `${(score / max) * 100}%`, height: '100%', borderRadius: '3px',
        background: color, transition: 'width 0.6s ease',
      }} />
    </div>
    <span style={{ fontSize: '12px', fontWeight: 700, color, minWidth: '28px', textAlign: 'right' }}>{score}</span>
  </div>
);

const CompareIndicator = ({ own, competitor }) => {
  const diff = own - competitor;
  if (diff > 5) return <TrendingUp size={14} style={{ color: '#22c55e' }} />;
  if (diff < -5) return <TrendingDown size={14} style={{ color: '#ef4444' }} />;
  return <Minus size={14} style={{ color: '#94a3b8' }} />;
};

export default function ConcurrentenPage() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ naam: '', website: '' });

  const fetchData = () => {
    setLoading(true);
    api('get', '/api/platform/concurrenten')
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const scanConcurrent = async (id) => {
    setScanning(s => ({ ...s, [id]: true }));
    try {
      await api('post', `/api/platform/concurrenten/${id}/scan`);
      toast.success('Concurrent gescand!');
      fetchData();
    } catch (e) {
      toast.error('Scan mislukt');
    }
    setScanning(s => ({ ...s, [id]: false }));
  };

  const startEdit = (conc) => {
    setEditing(conc.id);
    setEditForm({ naam: conc.naam, website: conc.website });
  };

  const saveEdit = async () => {
    const updated = (data?.concurrenten || []).map(c =>
      c.id === editing ? { ...c, naam: editForm.naam, website: editForm.website } : c
    );
    try {
      await api('post', '/api/platform/concurrenten/update', { concurrenten: updated });
      toast.success('Opgeslagen');
      setEditing(null);
      fetchData();
    } catch (e) {
      toast.error('Opslaan mislukt');
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const concurrenten = data?.concurrenten || [];
  const ownScore = data?.own_score || 0;
  const ownCompany = data?.own_company || 'Uw bedrijf';

  return (
    <div data-testid="concurrenten-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
          Concurrentie Analyse
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Vergelijk uw online prestaties met die van uw directe concurrenten.
        </p>
      </div>

      {/* Score vergelijking */}
      <div style={{
        background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9',
        padding: '24px', marginBottom: '20px',
      }}>
        <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '20px' }}>
          Score Vergelijking
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Eigen bedrijf */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '14px',
            background: 'rgba(212,160,23,0.04)', borderRadius: '12px', border: '1px solid rgba(212,160,23,0.15)',
          }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
              background: '#D4A017', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Zap size={18} color="#0f172a" />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{ownCompany}</p>
              <p style={{ fontSize: '11px', color: '#D4A017', fontWeight: 600 }}>Uw bedrijf</p>
            </div>
            <div style={{
              fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800,
              color: ownScore >= 60 ? '#22c55e' : ownScore >= 40 ? '#D4A017' : '#ef4444',
            }}>
              {ownScore}
            </div>
          </div>

          {/* Concurrenten */}
          {concurrenten.map(conc => {
            const concScore = conc.scan_data?.score || 0;
            const isAhead = ownScore > concScore;

            return (
              <div key={conc.id} data-testid={`concurrent-row-${conc.id}`} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '14px',
                background: 'white', borderRadius: '12px', border: '1px solid #f1f5f9',
              }}>
                <div style={{
                  width: '40px', height: '40px', borderRadius: '10px', flexShrink: 0,
                  background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Globe size={18} color="#64748b" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editing === conc.id ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <input value={editForm.naam} onChange={e => setEditForm(f => ({ ...f, naam: e.target.value }))}
                        style={{ fontSize: '13px', fontWeight: 600, border: '1px solid #D4A017', borderRadius: '6px', padding: '4px 8px', flex: 1 }} />
                      <input value={editForm.website} onChange={e => setEditForm(f => ({ ...f, website: e.target.value }))}
                        placeholder="website.nl"
                        style={{ fontSize: '11px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '4px 8px', flex: 1 }} />
                      <button onClick={saveEdit} style={{ background: '#22c55e', border: 'none', borderRadius: '6px', padding: '4px', cursor: 'pointer' }}>
                        <Check size={14} color="white" />
                      </button>
                      <button onClick={() => setEditing(null)} style={{ background: '#ef4444', border: 'none', borderRadius: '6px', padding: '4px', cursor: 'pointer' }}>
                        <X size={14} color="white" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conc.naam}
                      </p>
                      <p style={{ fontSize: '11px', color: '#3b82f6' }}>{conc.website || 'Geen website'}</p>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {conc.scan_data ? (
                    <>
                      <CompareIndicator own={ownScore} competitor={concScore} />
                      <span style={{
                        fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800,
                        color: concScore >= 60 ? '#22c55e' : concScore >= 40 ? '#D4A017' : '#ef4444',
                      }}>
                        {concScore}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>Niet gescand</span>
                  )}
                  <button
                    data-testid={`scan-concurrent-${conc.id}`}
                    onClick={() => scanConcurrent(conc.id)}
                    disabled={scanning[conc.id]}
                    style={{
                      background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '6px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center',
                    }}
                    title="Scan concurrent"
                  >
                    <RefreshCw size={14} style={{
                      color: '#64748b',
                      animation: scanning[conc.id] ? 'spin 1s linear infinite' : 'none',
                    }} />
                  </button>
                  <button onClick={() => startEdit(conc)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    <Edit3 size={14} style={{ color: '#94a3b8' }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail vergelijking per module */}
      {concurrenten.some(c => c.scan_data) && (
        <div style={{
          background: 'white', borderRadius: '16px', border: '1px solid #f1f5f9',
          padding: '24px', marginBottom: '20px',
        }}>
          <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 700, color: '#0f172a', marginBottom: '20px' }}>
            Gedetailleerde Vergelijking
          </h2>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', fontSize: '11px', color: '#94a3b8', fontWeight: 600, padding: '0 8px 8px' }}>Module</th>
                  <th style={{ textAlign: 'left', fontSize: '11px', color: '#D4A017', fontWeight: 600, padding: '0 8px 8px' }}>{ownCompany}</th>
                  {concurrenten.filter(c => c.scan_data).map(c => (
                    <th key={c.id} style={{ textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: 600, padding: '0 8px 8px' }}>
                      {c.naam}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {MODULE_KEYS.map(mod => {
                  const Icon = mod.icon;
                  return (
                    <tr key={mod.key} style={{ background: '#f8fafc', borderRadius: '8px' }}>
                      <td style={{ padding: '10px 8px', borderRadius: '8px 0 0 8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon size={14} style={{ color: mod.color }} />
                          <span style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a' }}>{mod.label}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px' }}>
                        <ScoreBar score={ownScore} color="#D4A017" />
                      </td>
                      {concurrenten.filter(c => c.scan_data).map(c => (
                        <td key={c.id} style={{ padding: '10px 8px', borderRadius: c === concurrenten[concurrenten.length - 1] ? '0 8px 8px 0' : undefined }}>
                          <ScoreBar score={c.scan_data?.score || 0} color={mod.color} />
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Scan all CTA */}
      {concurrenten.some(c => !c.scan_data) && (
        <div style={{
          background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '14px',
          padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '12px',
        }}>
          <div>
            <p style={{ color: '#D4A017', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
              Concurrent Analyse
            </p>
            <p style={{ color: 'white', fontWeight: 700, fontSize: '14px' }}>
              Scan alle concurrenten om te vergelijken
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '2px' }}>
              We analyseren hun websites automatisch
            </p>
          </div>
          <button
            data-testid="scan-all-btn"
            onClick={async () => {
              for (const c of concurrenten) {
                if (!c.scan_data && c.website) await scanConcurrent(c.id);
              }
            }}
            style={{
              background: '#D4A017', color: '#0f172a', fontWeight: 700,
              padding: '10px 20px', borderRadius: '10px', border: 'none',
              cursor: 'pointer', fontSize: '13px',
            }}
          >
            Scan alle concurrenten <RefreshCw size={14} style={{ display: 'inline', marginLeft: '4px' }} />
          </button>
        </div>
      )}

      {concurrenten.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: '#94a3b8' }}>
          <Globe size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px' }}>Nog geen concurrenten gevonden. Start een scan om uw concurrenten te ontdekken.</p>
        </div>
      )}
    </div>
  );
}
